import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';

import { AuthService } from './auth.service';
import { FundService } from './fund/fund.service';
import { GeneralService } from './general';
import { GsUserService } from './user/gs-user.service';
import { UtmAttributionService } from './utm-attribution.service';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private initialized = false;
  private previousUrl: string | null = null;
  private _userType: string = 'Anonymous';

  /**
   * Static URL → page-title map.  Used to resolve titles for all named routes.
   * Charity/project detail pages are intentionally excluded here — those
   * components call `trackPageView` directly after their entity data loads,
   * so they can send the real entity name as the page title.
   */
  private readonly _pageTitleMap: { pattern: RegExp; title: string }[] = [
    { pattern: /^\/home(\/|$)/, title: 'GivingSpace | Home' },
    { pattern: /^\/about(\/|$)/, title: 'GivingSpace | About' },
    { pattern: /^\/contact(\/|$)/, title: 'GivingSpace | Contact Us' },
    { pattern: /^\/charities(\/|$|\?)/, title: 'GivingSpace | Charity Catalog' },
    { pattern: /^\/projects(\/|$|\?)/, title: 'GivingSpace | Project Catalog' },
    { pattern: /^\/search(\/|$|\?)/, title: 'GivingSpace | Search Results' },

    // Settings — My Profile section
    { pattern: /^\/settings\/information(\/|$)/, title: 'GivingSpace | My Profile | Information' },
    { pattern: /^\/settings\/security(\/|$)/, title: 'GivingSpace | My Profile | Security' },
    { pattern: /^\/settings\/favorites(\/|$)/, title: 'GivingSpace | My Profile | Favorites' },
    {
      pattern: /^\/settings\/my-champions(\/|$)/,
      title: 'GivingSpace | My Profile | Championed Charities',
    },

    // Settings — Charity Profile section
    {
      pattern: /^\/settings\/charities\/edit(\/|$)/,
      title: 'GivingSpace | Charity Profile | Charities',
    },
    {
      pattern: /^\/settings\/charities(\/|$|\?)/,
      title: 'GivingSpace | Charity Profile | Charities',
    },
    { pattern: /^\/settings\/projects(\/|$)/, title: 'GivingSpace | Charity Profile | Projects' },
    {
      pattern: /^\/settings\/portfolios(\/|$)/,
      title: 'GivingSpace | Charity Profile | Portfolios',
    },
    { pattern: /^\/settings\/users(\/|$)/, title: 'GivingSpace | Charity Profile | Users' },
    { pattern: /^\/settings\/champions(\/|$)/, title: 'GivingSpace | Charity Profile | Champions' },
    { pattern: /^\/settings\/championed-charities-admin(\/|$)/, title: 'GivingSpace | Charity Profile | Championed Charities' },
    { pattern: /^\/settings\/global-settings(\/|$)/, title: 'GivingSpace | Charity Profile | Global Settings' },
    { pattern: /^\/settings\/resources(\/|$)/, title: 'GivingSpace | Charity Profile | Resources' },

    // Settings — Fund Profile section
    {
      pattern: /^\/settings\/fund-settings(\/|$)/,
      title: 'GivingSpace | Fund Profile | Fund Settings',
    },

    // Portal — Fund pages  (all under /fund/)
    { pattern: /^\/fund\/dashboard(\/|$)/, title: 'GivingSpace | Fund Dashboard' },
    { pattern: /^\/fund\/contribute(\/|$)/, title: 'GivingSpace | Contribute' },
    { pattern: /^\/fund\/grant\/add-charity(\/|$)/, title: 'GivingSpace | Grant | Add Charity' },
    {
      pattern: /^\/fund\/grant\/manage-recurring(\/|$)/,
      title: 'GivingSpace | Grant | Manage Recurring',
    },
    { pattern: /^\/fund\/grant(\/|$)/, title: 'GivingSpace | Grant' },
    {
      pattern: /^\/fund\/distribute\/manage-recurring(\/|$)/,
      title: 'GivingSpace | Distribute | Manage Recurring',
    },
    { pattern: /^\/fund\/distribute(\/|$)/, title: 'GivingSpace | Distribute' },
    { pattern: /^\/fund\/transfer(\/|$)/, title: 'GivingSpace | Transfer' },
    { pattern: /^\/fund\/activity(\/|$)/, title: 'GivingSpace | Activity' },
    { pattern: /^\/fund\/currency-conversion(\/|$)/, title: 'GivingSpace | Currency Conversion' },
    {
      pattern: /^\/fund\/manage-recurring\?type=distribution/,
      title: 'GivingSpace | Manage Recurring Distributions',
    },
    {
      pattern: /^\/fund\/manage-recurring\?type=grant/,
      title: 'GivingSpace | Manage Recurring Grants',
    },
    { pattern: /^\/fund\/manage-recurring(\/|$)/, title: 'GivingSpace | Manage Recurring' },
    {
      pattern: /^\/fund\/donate\/manage-recurring(\/|$)/,
      title: 'GivingSpace | Donate | Manage Recurring',
    },
    { pattern: /^\/fund\/donate\/confirm(\/|$)/, title: 'GivingSpace | Donate | Confirmation' },
    { pattern: /^\/fund\/donate\/cancel(\/|$)/, title: 'GivingSpace | Donate | Cancellation' },
    {
      pattern: /^\/fund\/donate\/[^/]+\/confirm(\/|$)/,
      title: 'GivingSpace | Donate | Confirmation',
    },
    {
      pattern: /^\/fund\/donate\/[^/]+\/cancel(\/|$)/,
      title: 'GivingSpace | Donate | Cancellation',
    },
    { pattern: /^\/fund\/donate(\/|$)/, title: 'GivingSpace | Contribute' },

    // Portal — Public donate (unauthenticated)
    {
      pattern: /^\/public\/donate\/manage-recurring(\/|$)/,
      title: 'GivingSpace | Donate | Manage Recurring',
    },
    { pattern: /^\/public\/donate\/confirm(\/|$)/, title: 'GivingSpace | Donate | Confirmation' },
    { pattern: /^\/public\/donate\/cancel(\/|$)/, title: 'GivingSpace | Donate | Cancellation' },
    {
      pattern: /^\/public\/donate\/[^/]+\/confirm(\/|$)/,
      title: 'GivingSpace | Donate | Confirmation',
    },
    {
      pattern: /^\/public\/donate\/[^/]+\/cancel(\/|$)/,
      title: 'GivingSpace | Donate | Cancellation',
    },
    { pattern: /^\/public\/donate(\/|$|\?)/, title: 'GivingSpace | Donate' },
  ];

  constructor(
    private generalService: GeneralService,
    private authService: AuthService,
    private gsUserService: GsUserService,
    private titleService: Title,
    private router: Router,
    private utmAttributionService: UtmAttributionService,
    private fundService: FundService
  ) {}

  /**
   * Call once after general settings are loaded (APP_INITIALIZER guarantees
   * this before ngOnInit).  Reads the GA tracking code from the platform
   * configuration, bootstraps gtag, fires the initial page_view, and then
   * subscribes to all subsequent NavigationEnd events.
   */
  initialize(): void {
    if (this.initialized) return;

    const settings = this.generalService.settings;
    if (!settings?.response?.globalConfig) return;

    const configMap: Record<string, string> = settings.response.globalConfig.reduce(
      (acc: Record<string, string>, item: { key: string; value: string }) => {
        acc[item.key] = item.value;
        return acc;
      },
      {}
    );

    const enabled = configMap['enableGoogleAnalytics'] === 'true';
    const trackingId = configMap['googleAnalyticsTrackingCode']?.trim();

    if (!enabled || !trackingId) return;

    this.loadScript(trackingId);
    this.initialized = true;

    // Fire the initial page_view for the page already loaded in the browser.
    // (The very first NavigationEnd fires during bootstrap, before ngOnInit,
    // so the router subscription below would miss it.)
    const initialUrl = this.router.url;
    const trackInitialPageView = () => {
      const initialTitle = this.resolvePageTitle(initialUrl);
      if (initialTitle) {
        this.titleService.setTitle(initialTitle);
        this.trackPageView(initialTitle, initialUrl);
      }
    };

    // Fetch userType from API if the user is authenticated, holding the initial
    // page_view until it resolves so a logged-in landing isn't sent as 'Anonymous'.
    if (this.authService.userUuid) {
      this.gsUserService.getUserInfo().subscribe({
        next: (response) => {
          this._userType = this.mapPageViewUserType(response?.response?.userType);
          trackInitialPageView();
        },
        error: () => {
          this._userType = 'Anonymous';
          trackInitialPageView();
        },
      });
    } else {
      trackInitialPageView();
    }

    // Fire a `login` event for a fresh login (OAuth callback or loginWithPassword success).
    // BehaviorSubject replays its current value, so this also fires for a login that happened
    // during app bootstrap, before this subscription existed.
    this.authService.freshLogin$.pipe(filter(Boolean)).subscribe(() => {
      this.authService.clearFreshLoginFlag();
      this.handleLoginEvent();
    });

    // Fire a `fund_switch` event whenever a user explicitly switches funds.
    this.fundService.fundSwitched$.subscribe(({ from, to }) => {
      this.trackFundSwitch(from, to);
    });

    // Subscribe to all future navigations.  This subscription lives for the
    // lifetime of the app (root service — never destroyed).
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const title = this.resolvePageTitle(event.urlAfterRedirects);
        if (title) {
          this.titleService.setTitle(title);
          this.trackPageView(title, event.urlAfterRedirects);
        }
        this.previousUrl = window.location.href;
      });
  }

  /**
   * Maps a URL path to its static page title.
   * Returns `undefined` for dynamic pages (charity/:id, project/:id) that
   * set their own title after entity data loads.
   */
  resolvePageTitle(url: string): string | undefined {
    const path = url.split('?')[0];
    return (
      this._pageTitleMap.find((entry) => entry.pattern.test(url)) ??
      this._pageTitleMap.find((entry) => entry.pattern.test(path))
    )?.title;
  }

  /** Sends a page_view event. Called by the internal subscription and by
   *  charity/project components once the entity name is available. */
  trackPageView(title: string, url: string): void {
    if (!this.initialized) return;
    this.gtag('event', 'page_view', {
      page_title: title,
      page_location: window.location.href,
      page_path: url,
      page_referrer: this.previousUrl ?? document.referrer ?? null,
      ...this.userParams(),
    });
  }

  /** Sends a button_click event. */
  trackButtonClick(label: string): void {
    if (!this.initialized) return;
    this.gtag('event', 'button_click', {
      event_category: 'button',
      event_label: label,
      page_title: this.titleService.getTitle(),
      ...this.userParams(),
    });
  }

  /** Sends a give_button event. */
  trackGiveButtonClick(clickId?: string): void {
    if (!this.initialized) return;
    this.gtag('event', 'give_button', {
      engagement: 'give_button',
      event_category: 'give_button',
      event_label: 'Give button click',
      value: 'Give button click',
      click_id: clickId ?? null,
      ...this.userParams(),
    });
  }

  /**
   * Sends a switchboard_impression event when the Switchboard card reaches
   * ~95% visibility in the viewport. Fired once per component mount. Total
   * events give Total Impressions; GSUserID from {@link userParams} enables
   * Unique Impressions. `impression_occurred_at` carries the time in ISO 8601 UTC.
   */
  trackSwitchboardImpression(): void {
    if (!this.initialized) return;
    this.gtag('event', 'switchboard_impression', {
      engagement: 'switchboard',
      event_category: 'switchboard',
      event_label: 'Switchboard card impression',
      value: 'Switchboard impression',
      impression_occurred_at: new Date().toISOString(),
      ...this.userParams(),
    });
  }

  /**
   * Sends a switchboard_click event for the Switchboard card's "Learn More"
   * button. Fired on every click: total events give Total Clicks, while the
   * GSUserID attached via {@link userParams} lets distinct users be counted as
   * Unique Clicks. `click_initiated_at` carries the click time in ISO 8601 UTC.
   */
  trackSwitchboardClick(): void {
    if (!this.initialized) return;
    this.gtag('event', 'switchboard_click', {
      engagement: 'switchboard',
      event_category: 'switchboard',
      event_label: 'Switchboard Learn More click',
      value: 'Switchboard click',
      click_initiated_at: new Date().toISOString(),
      ...this.userParams(),
    });
  }

  /** Sends a share event for social platform or copy-link actions. */
  trackShareClick(platform: string): void {
    if (!this.initialized) return;
    this.gtag('event', 'share', {
      engagement: 'Share',
      event_category: 'Share',
      event_label: `${platform} click`,
      value: 'Share click',
      ...this.userParams(),
    });
  }

  /** Sends a link_click event. */
  trackLinkClick(linkUrl: string, label: string): void {
    if (!this.initialized) return;
    this.gtag('event', 'link_click', {
      event_category: 'link',
      event_label: label,
      link_url: linkUrl,
      page_title: this.titleService.getTitle(),
      ...this.userParams(),
    });
  }

  /**
   * Sends a login event. Fired at most once per fresh login (see {@link handleLoginEvent}) —
   * never on reload, silent refresh, or token refresh.
   */
  trackLogin(userType: string, fundRole: string, defaultFund: string): void {
    if (!this.initialized) return;
    this.gtag('event', 'login', {
      engagement: 'login',
      event_category: 'authentication',
      event_label: 'User login',
      user_type: userType,
      fund_role: fundRole,
      default_fund: defaultFund,
      GSUserID: this.authService.userUuid,
    });
  }

  /**
   * Sends a fund_switch event. Fired for explicit user-driven fund switches only — see
   * {@link FundService.fundSwitched$}. Only Fund Management users can switch funds, so
   * user_type is always 'Fund Holder' here (same rule as the login event) rather than
   * userParams()'s generic user_type, which defaults to 'Anonymous'.
   */
  trackFundSwitch(fromFund: string, toFund: string): void {
    if (!this.initialized) return;
    this.gtag('event', 'fund_switch', {
      engagement: 'fund_switch',
      event_category: 'authentication',
      from_fund: fromFund,
      to_fund: toFund,
      ...this.userParams(),
      user_type: 'Fund Holder',
    });
  }

  /**
   * Sends a form_start event: the user made their first genuine edit on this
   * form step (tab). Callers must guard this to fire at most once per
   * form/step per editing session — never on programmatic data population.
   */
  trackFormStart(formName: string, formStep: number, engagement?: string): void {
    if (!this.initialized) return;
    this.gtag('event', 'form_start', {
      form_name: formName,
      form_step: formStep,
      ...(engagement ? { engagement } : {}),
      ...this.userParams(),
    });
  }

  /**
   * Sends a form_complete event: this form step's required fields were
   * satisfied and the user moved on from it (or completed the overall form
   * while it was the active step).
   */
  trackFormComplete(formName: string, formStep: number, engagement?: string): void {
    if (!this.initialized) return;
    this.gtag('event', 'form_complete', {
      form_name: formName,
      form_step: formStep,
      ...(engagement ? { engagement } : {}),
      ...this.userParams(),
    });
  }

  /**
   * Sends a form_partial event: this form step was still in progress
   * (started but not completed) when the user saved the overall form as a
   * draft.
   */
  trackFormPartial(formName: string, formStep: number, engagement?: string): void {
    if (!this.initialized) return;
    this.gtag('event', 'form_partial', {
      form_name: formName,
      form_step: formStep,
      ...(engagement ? { engagement } : {}),
      ...this.userParams(),
    });
  }

  /**
   * Sends a form_abandon event: this form step was still in progress
   * (started but not completed) when the user exited the overall form
   * without saving.
   */
  trackFormAbandon(formName: string, formStep: number, engagement?: string): void {
    if (!this.initialized) return;
    this.gtag('event', 'form_abandon', {
      form_name: formName,
      form_step: formStep,
      ...(engagement ? { engagement } : {}),
      ...this.userParams(),
    });
  }

  /**
   * Sends a form_error event: either a specific field on this form step
   * failed validation (or a save/upload attempt tied to that field failed),
   * or an API call made while on this step returned an error. `errorField`
   * is optional — omit it for errors not tied to one specific control (e.g.
   * an API response failure). `errorMessage` carries the user-facing message
   * when it originates from an API response.
   */
  trackFormError(
    formName: string,
    formStep: number,
    errorField?: string,
    engagement?: string,
    errorMessage?: string
  ): void {
    if (!this.initialized) return;
    this.gtag('event', 'form_error', {
      form_name: formName,
      form_step: formStep,
      ...(errorField ? { error_field: errorField } : {}),
      ...(engagement ? { engagement } : {}),
      ...(errorMessage ? { error_message: errorMessage } : {}),
      ...this.userParams(),
    });
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Resolves user_type/fund_role/default_fund for the login event that just occurred, then
   * fires trackLogin(). For Fund Management users, waits for FundService.loadFunds() to resolve
   * so default_fund can ride on the same event — if that load fails, login still fires with an
   * empty default_fund (a login event must not be blocked by a fund-load failure).
   */
  private async handleLoginEvent(): Promise<void> {
    const userType = await this.resolveUserType();
    const hasPortalAccess = this.authService.hasPortalAccess();
    const fundRole = hasPortalAccess ? this.resolveFundRole() : '';
    let defaultFund = '';

    if (hasPortalAccess) {
      try {
        await this.fundService.loadFunds();
        defaultFund = this.fundService.getFundName();
      } catch {
        // Leave default_fund empty if fund load fails — login itself still fires.
      }
    }

    this.trackLogin(userType, fundRole, defaultFund);
  }

  /**
   * Derives the login-event user_type (Donor / Charity / Fund Holder / Admin). GS_SUPERADMIN and
   * GS_ADMIN_FOUNDATION (the existing "site admin" check — see users-list.component.ts) override
   * everything else. Any Fund Management portal access (PORTAL_ADMIN or PORTAL_USER) defaults to
   * Fund Holder regardless of admin/user split — PORTAL_ADMIN here means "full-access DAF holder,"
   * not platform staff. GS-only users fall back to the backend userType registration enum.
   */
  private async resolveUserType(): Promise<string> {
    if (
      this.authService.hasRole('GS_SUPERADMIN') ||
      this.authService.hasRole('GS_ADMIN_FOUNDATION')
    ) {
      return 'Admin';
    }
    if (this.authService.hasPortalAccess()) {
      return 'Fund Holder';
    }

    try {
      const info = await firstValueFrom(this.gsUserService.getUserInfo());
      switch (info?.response?.userType) {
        case 'DONOR':
          return 'Donor';
        case 'ORGANISATION':
          return 'Charity';
        case 'FOUNDATION':
          return 'Fund Holder';
        default:
          return 'Donor';
      }
    } catch {
      return 'Donor';
    }
  }

  /**
   * Maps the backend userType registration enum (get-user-info) to the page_view
   * vocabulary (GIV-5539): Foundation, Charity, Donor or Anonymous. Unknown values
   * fall back to 'Anonymous' rather than leaking the raw enum into analytics.
   */
  private mapPageViewUserType(userType: string | null | undefined): string {
    switch (userType) {
      case 'FOUNDATION':
        return 'Foundation';
      case 'ORGANISATION':
        return 'Charity';
      case 'DONOR':
        return 'Donor';
      default:
        return 'Anonymous';
    }
  }

  /** Derives the login-event fund_role from the user's JWT roles. Empty for non-FM users. */
  private resolveFundRole(): string {
    if (this.authService.hasRole('PORTAL_ADMIN')) return 'PORTAL_ADMIN';
    if (this.authService.hasPortalUserRole()) return 'PORTAL_USER';
    return '';
  }

  private loadScript(trackingId: string): void {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    // Must use function declaration + arguments (not rest params) so Google's
    // script can correctly read the Arguments object from the dataLayer queue.
    // eslint-disable-next-line prefer-rest-params
    window.gtag = function () {
      window.dataLayer.push(arguments);
    } as typeof window.gtag;
    window.gtag('js', new Date());
    // Disable the automatic page_view fired by gtag — we send it manually so
    // we can attach user_type / GSUserID to every hit.
    window.gtag('config', trackingId, { send_page_view: false, transport_type: 'beacon' });
  }

  private userParams(): Record<string, string | null> {
    // Attach the session's media-kit attribution (if any) so every hit remains
    // attributable to the media-kit promotional tools even after the landing
    // URL's utm_source/signature have been dropped by in-app navigation.
    const attribution = this.utmAttributionService.getAttribution();
    return {
      user_type: this.getUserType(),
      GSUserID: this.authService.userUuid,
      ...(attribution
        ? { utm_source: attribution.utm_source, signature: attribution.signature }
        : {}),
    };
  }

  private getUserType(): string {
    return this._userType;
  }

  private gtag(...args: any[]): void {
    if (typeof window.gtag === 'function') {
      window.gtag(...args);
    }
  }
}
